// Experiment Tracking Utilities for Transfer and Learning Analysis
// Captures timing between tasks and learning control variables

interface TaskTransition {
  fromTask: string;
  toTask: string;
  transitionTime: number;
  fillerTaskDuration: number;
  participantId: string;
  timestamp: number;
}

interface LearningControlData {
  participantId: string;
  blockOrder: 'email-first' | 'device-first';
  scenarioPosition: number; // 1-10 within block
  blockNumber: number; // 1 or 2
  cumulativeTrials: number; // Total trials across both tasks
  timeInTask: number; // Milliseconds spent in current task
  previousAccuracy: number; // Running accuracy
  previousGistRate: number; // Running gist-based decision rate
}

export class ExperimentTracker {
  private taskTransitions: TaskTransition[] = [];
  private learningData: LearningControlData[] = [];
  private taskStartTimes: Map<string, number> = new Map();
  private participantMetrics: Map<string, any> = new Map();

  // Track when a task starts
  startTask(taskName: string, participantId: string) {
    const now = Date.now();
    this.taskStartTimes.set(`${participantId}-${taskName}`, now);
    
    // Log task start for transition tracking
    const lastTask = this.getLastCompletedTask(participantId);
    if (lastTask) {
      const transitionTime = now - lastTask.endTime;
      this.recordTransition(lastTask.taskName, taskName, transitionTime, participantId);
    }
  }

  // Track when a task ends
  endTask(taskName: string, participantId: string) {
    const startKey = `${participantId}-${taskName}`;
    const startTime = this.taskStartTimes.get(startKey);
    
    if (startTime) {
      const duration = Date.now() - startTime;
      // Store task duration in participant metrics
      const metrics = this.participantMetrics.get(participantId) || {};
      metrics[`${taskName}Duration`] = duration;
      metrics[`${taskName}EndTime`] = Date.now();
      this.participantMetrics.set(participantId, metrics);
    }
  }

  // Get task end time for a participant
  private getTaskEndTime(participantId: string, taskName: string): number | undefined {
    const metrics = this.participantMetrics.get(participantId);
    return metrics ? metrics[`${taskName}EndTime`] : undefined;
  }

  // Record transition between tasks
  private recordTransition(
    fromTask: string, 
    toTask: string, 
    transitionTime: number, 
    participantId: string
  ) {
    // Calculate filler task duration if applicable
    let fillerTaskDuration = 0;
    if (fromTask === 'email-task' && toTask === 'device-task') {
      // They went through filler task
      const fillerStart = this.taskStartTimes.get(`${participantId}-filler-task`);
      const fillerEnd = this.getTaskEndTime(participantId, 'filler-task');
      if (fillerStart && fillerEnd) {
        fillerTaskDuration = fillerEnd - fillerStart;
      }
    }

    this.taskTransitions.push({
      fromTask,
      toTask,
      transitionTime,
      fillerTaskDuration,
      participantId,
      timestamp: Date.now()
    });
  }

  // Track learning control variables for each scenario
  trackScenarioAttempt(
    participantId: string,
    taskType: 'email' | 'device',
    scenarioIndex: number,
    response: any,
    conditions: any
  ) {
    const blockOrder = conditions.taskOrder || 'email-first';
    const isFirstBlock = (blockOrder === 'email-first' && taskType === 'email') ||
                       (blockOrder === 'device-first' && taskType === 'device');
    
    const blockNumber = isFirstBlock ? 1 : 2;
    const cumulativeTrials = this.getCumulativeTrials(participantId);
    const timeInTask = this.getTimeInCurrentTask(participantId, taskType);
    const metrics = this.participantMetrics.get(participantId) || {};
    
    const learningControl: LearningControlData = {
      participantId,
      blockOrder,
      scenarioPosition: scenarioIndex + 1, // 1-indexed
      blockNumber,
      cumulativeTrials,
      timeInTask,
      previousAccuracy: metrics.runningAccuracy || 0,
      previousGistRate: metrics.runningGistRate || 0
    };

    this.learningData.push(learningControl);
    
    // Update running metrics
    this.updateRunningMetrics(participantId, response);
  }

  // Get time spent in current task
  private getTimeInCurrentTask(participantId: string, taskType: string): number {
    const startTime = this.taskStartTimes.get(`${participantId}-${taskType}-task`);
    return startTime ? Date.now() - startTime : 0;
  }

  // Get cumulative trials across all tasks
  private getCumulativeTrials(participantId: string): number {
    return this.learningData.filter(d => d.participantId === participantId).length;
  }

  // Update running accuracy and gist rate
  private updateRunningMetrics(participantId: string, response: any) {
    const metrics = this.participantMetrics.get(participantId) || {
      totalDecisions: 0,
      correctDecisions: 0,
      gistDecisions: 0,
      runningAccuracy: 0,
      runningGistRate: 0
    };

    metrics.totalDecisions++;
    if (response.isCorrect) metrics.correctDecisions++;
    if (response.processingMode === 'gist' || response.processingMode === 'automatic') {
      metrics.gistDecisions++;
    }

    metrics.runningAccuracy = metrics.correctDecisions / metrics.totalDecisions;
    metrics.runningGistRate = metrics.gistDecisions / metrics.totalDecisions;

    this.participantMetrics.set(participantId, metrics);
  }

  // Get last completed task for a participant
  private getLastCompletedTask(participantId: string): { taskName: string; endTime: number } | null {
    // Implementation would check task completion records
    return null; // Placeholder
  }


  // Export data for analysis
  exportTransferData() {
    return {
      transitions: this.taskTransitions,
      learningControls: this.learningData,
      summary: this.generateTransferSummary()
    };
  }

  // Generate summary statistics for transfer analysis
  private generateTransferSummary() {
    const avgTransitionTime = this.taskTransitions.reduce(
      (sum, t) => sum + t.transitionTime, 0
    ) / this.taskTransitions.length;

    const avgFillerDuration = this.taskTransitions
      .filter(t => t.fillerTaskDuration > 0)
      .reduce((sum, t) => sum + t.fillerTaskDuration, 0) / 
      this.taskTransitions.filter(t => t.fillerTaskDuration > 0).length;

    return {
      totalTransitions: this.taskTransitions.length,
      avgTransitionTime,
      avgFillerDuration,
      taskOrderDistribution: this.getTaskOrderDistribution()
    };
  }

  // Get distribution of task orders
  private getTaskOrderDistribution() {
    const distribution: { [key: string]: number } = {};
    
    this.learningData.forEach(data => {
      distribution[data.blockOrder] = (distribution[data.blockOrder] || 0) + 1;
    });

    return distribution;
  }

  // Integration with existing tracker
  integrateWithTracker(tracker: any) {
    // Add hooks to existing tracker
    const originalTrack = tracker.track.bind(tracker);
    
    tracker.track = (eventType: string, data: any) => {
      // Intercept relevant events
      if (eventType === 'phaseStart') {
        this.startTask(data.phase, data.participantId || 'unknown');
      } else if (eventType === 'phaseEnd') {
        this.endTask(data.phase, data.participantId || 'unknown');
      } else if (eventType === 'scenarioResponse' || eventType === 'emailAction') {
        this.trackScenarioAttempt(
          data.participantId || 'unknown',
          data.taskType || (eventType === 'emailAction' ? 'email' : 'device'),
          data.scenarioIndex || 0,
          data,
          data.conditions || {}
        );
      }
      
      // Call original tracker
      return originalTrack(eventType, data);
    };
  }
}

// Singleton instance
export const experimentTracker = new ExperimentTracker();

// Helper functions for analysis models

export function prepareTransferAnalysisData() {
  const data = experimentTracker.exportTransferData();
  
  // Format for R/statistical analysis
  return {
    // Transfer timing model data
    transferTiming: data.transitions.map(t => ({
      participant_id: t.participantId,
      time_between_tasks: t.transitionTime,
      filler_duration: t.fillerTaskDuration,
      from_task: t.fromTask,
      to_task: t.toTask
    })),
    
    // Learning control model data
    learningControls: data.learningControls.map((d: LearningControlData) => ({
      participant_id: d.participantId,
      block_order: d.blockOrder,
      scenario_position: d.scenarioPosition,
      block_number: d.blockNumber,
      cumulative_trials: d.cumulativeTrials,
      time_in_task: d.timeInTask,
      previous_accuracy: d.previousAccuracy,
      previous_gist_rate: d.previousGistRate
    }))
  };
}

// Export for use in analysis
export default experimentTracker;